"use client";

import Image from "next/image";
import {
  Sidebar as SidebarShell,
  SidebarFooter,
  SidebarHeader,
} from "@/components/layout/sidebar/sidebar-layout";
import { SidebarMainContent } from "@/components/layout/sidebar/main-content";
import { SidebarFooterContent } from "@/components/layout/sidebar/footer";
import { MobileSidebarPanels } from "@/components/layout/sidebar/mobile/mobile-panels";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSidebar } from "@/stores/sidebar-store";
import useIsMobile from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

export function Sidebar() {
  const { expanded, toggleSidebar, open } = useSidebar();
  const isMobile = useIsMobile();
  const sidebarExpanded = isMobile ? true : expanded;

  return (
    <SidebarShell>
      <SidebarHeader
        className={cn(
          // Brand mark + panel toggle. Matches the 43px navbar, offset by the
          // inset panel's 8px top gutter (`lg:py-2` on SidebarInset), so the
          // mark shares the breadcrumb title's vertical centre.
          // Mobile drawer keeps a hairline under the header; on desktop the
          // sidebar is part of the window frame, so no full-width rules.
          "h-[43px] flex-row items-center overflow-hidden border-b border-border lg:mt-2 lg:border-b-0",
          // `pl-[22px]` lines the mark up with the icons in the rows below,
          // which sit behind the section's 14px padding plus 8px of their own.
          sidebarExpanded
            ? "justify-between pl-[22px] pr-3.5"
            : "justify-center px-0",
        )}
      >
        <Image
          src="/favicon.svg"
          alt="Arbi"
          width={36}
          height={28}
          priority
          className="h-6 w-auto shrink-0"
        />

        {!isMobile && sidebarExpanded && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleSidebar}
                className={cn(
                  "flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md",
                  "text-contrast/40 transition-colors duration-150 hover:bg-contrast/4 hover:text-heading",
                  "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
                )}
                aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
              >
                <Icon
                  name={
                    open
                      ? "IconSidebarSimpleLeftSquare"
                      : "IconSidebarSimpleRightSquare"
                  }
                  size={16}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              {open ? "Collapse sidebar" : "Keep sidebar open"}
            </TooltipContent>
          </Tooltip>
        )}
      </SidebarHeader>

      {/* No outer scroller on desktop: the nav stays put and the searches
          list scrolls inside its own section. The mobile drawer scrolls as
          one panel (see MobileSidebarPanels). */}
      {isMobile ? <MobileSidebarPanels /> : <SidebarMainContent />}

      {!isMobile && (
        <SidebarFooter>
          <SidebarFooterContent expanded={sidebarExpanded} />
        </SidebarFooter>
      )}
    </SidebarShell>
  );
}
